import { supabase } from '../lib/supabase';
import { getSubdomain } from '../utils/subdomain';

/**
 * Organization Service
 * Handles multi-tenant organization operations for users
 */

const organizationService = {
  /**
   * Get all organizations for current user
   */
  async getUserOrganizations() {
    try {
      const subdomain = getSubdomain();
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's profile to find their tenant
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)
        ?.maybeSingle();

      if (profileError) throw profileError;
      if (!profile) throw new Error('User profile not found');

      // Get user's role assignments to find all accessible tenants
      const { data: roleAssignments, error: rolesError } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          tenant_id,
          role:system_roles(hierarchy_level)
        `)
        ?.eq('user_id', user?.id);

      if (rolesError) throw rolesError;

      // Check if user is super admin (can access all tenants)
      const isSuperAdmin = roleAssignments?.some(ra => ra?.role?.hierarchy_level >= 100);

      let tenantIds = [];
      if (isSuperAdmin) {
        // Super admin can see all tenants
        const { data: allTenants, error: tenantsError } = await supabase
          ?.from('tenants')
          ?.select('id');
        
        if (tenantsError) throw tenantsError;
        tenantIds = allTenants?.map(t => t?.id);
      } else {
        // Regular users see only their assigned tenants
        tenantIds = [...new Set(roleAssignments?.map(ra => ra?.tenant_id)?.filter(Boolean))];
        
        // Always include user's primary tenant
        if (profile?.tenant_id && !tenantIds?.includes(profile?.tenant_id)) {
          tenantIds?.push(profile?.tenant_id);
        }
      }

      // Return empty array if no tenants found
      if (!tenantIds || tenantIds?.length === 0) {
        return [];
      }

      // Fetch tenant details
      const { data: tenants, error: tenantsError } = await supabase
        ?.from('tenants')?.select('*')?.in('id', tenantIds);

      if (tenantsError) throw tenantsError;

      // Transform to expected format
      const organizations = tenants?.map(tenant => ({
        tenantId: tenant?.id,
        tenantName: tenant?.name,
        tenantSubdomain: tenant?.subdomain,
        tenantLogoUrl: tenant?.logo_url,
        isPrimary: tenant?.id === profile?.tenant_id
      }));
      
      // If on subdomain, prioritize that tenant
      if (subdomain) {
        const currentTenant = organizations?.find(org => org?.tenantSubdomain === subdomain);
        if (currentTenant) {
          return [currentTenant, ...organizations?.filter(org => org?.tenantSubdomain !== subdomain)];
        }
      }

      return organizations;
    } catch (error) {
      console.error('Error fetching user organizations:', error);
      throw error;
    }
  },

  /**
   * Switch the current user's active organization
   * @param {string} tenantId - The tenant ID to switch to
   * @returns {Promise<boolean>} Success status
   */
  async switchOrganization(tenantId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.rpc('switch_user_organization', {
          p_user_id: user?.id,
          p_tenant_id: tenantId
        });

      if (error) throw error;

      return data === true;
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  },

  /**
   * Set an organization as the user's primary/default organization
   * @param {string} tenantId - The tenant ID to set as primary
   * @returns {Promise<boolean>} Success status
   */
  async setPrimaryOrganization(tenantId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.rpc('set_primary_organization', {
          p_user_id: user?.id,
          p_tenant_id: tenantId
        });

      if (error) throw error;

      return data === true;
    } catch (error) {
      console.error('Error setting primary organization:', error);
      throw error;
    }
  },

  /**
   * Get user's organization memberships with full details
   * @returns {Promise<Array>} List of memberships
   */
  async getUserMemberships() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('user_organization_memberships')
        ?.select(`
          id,
          tenant_id,
          is_primary,
          last_accessed_at,
          joined_at,
          tenant:tenants(
            id,
            name,
            subdomain,
            logo_url,
            industry:company_industries(
              name
            )
          )
        `)
        ?.eq('user_id', user?.id)?.order('is_primary', { ascending: false })
        ?.order('last_accessed_at', { ascending: false, nullsFirst: false });

      if (error) throw error;

      // Transform to camelCase
      return data?.map(membership => ({
        id: membership?.id,
        tenantId: membership?.tenant_id,
        isPrimary: membership?.is_primary,
        lastAccessedAt: membership?.last_accessed_at,
        joinedAt: membership?.joined_at,
        tenant: {
          id: membership?.tenant?.id,
          name: membership?.tenant?.name,
          subdomain: membership?.tenant?.subdomain,
          logoUrl: membership?.tenant?.logo_url,
          industryName: membership?.tenant?.industry?.name
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching user memberships:', error);
      throw error;
    }
  },

  /**
   * Add user to a new organization (for invitation acceptance)
   * @param {string} tenantId - The tenant ID to join
   * @param {boolean} isPrimary - Whether this should be the primary organization
   * @returns {Promise<Object>} Created membership
   */
  async addUserToOrganization(tenantId, isPrimary = false) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('user_organization_memberships')
        ?.insert({
          user_id: user?.id,
          tenant_id: tenantId,
          is_primary: isPrimary,
          last_accessed_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        tenantId: data?.tenant_id,
        isPrimary: data?.is_primary,
        lastAccessedAt: data?.last_accessed_at,
        joinedAt: data?.joined_at
      };
    } catch (error) {
      console.error('Error adding user to organization:', error);
      throw error;
    }
  },

  /**
   * Switch organization context for Super Admin
   * Updates user profile tenant_id and organization membership
   * @param {string} targetTenantId - The tenant ID to switch to
   * @returns {Promise<boolean>} Success status
   */
  async switchOrganizationContext(targetTenantId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Check if user already has membership in target organization
      const { data: existingMembership } = await supabase
        ?.from('user_organization_memberships')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', targetTenantId)
        ?.single();

      // If no membership exists, create one
      if (!existingMembership) {
        const { error: membershipError } = await supabase
          ?.from('user_organization_memberships')
          ?.insert({
            user_id: user?.id,
            tenant_id: targetTenantId,
            is_primary: false,
            last_accessed_at: new Date()?.toISOString()
          });

        if (membershipError) throw membershipError;
      } else {
        // Update last accessed time
        const { error: updateError } = await supabase
          ?.from('user_organization_memberships')
          ?.update({ last_accessed_at: new Date()?.toISOString() })
          ?.eq('id', existingMembership?.id);

        if (updateError) throw updateError;
      }

      // Update user profile tenant_id
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ tenant_id: targetTenantId })
        ?.eq('id', user?.id);

      if (profileError) throw profileError;

      return true;
    } catch (error) {
      console.error('Error switching organization context:', error);
      throw error;
    }
  },

  /**
   * Get all organizations (Super Admin only)
   * @returns {Promise<Array>} List of all organizations
   */
  async getAllOrganizations() {
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.select(`
          id,
          name,
          subdomain,
          logo_url,
          is_active,
          created_at,
          industry:company_industries(name),
          subscription:tenant_subscriptions(
            status,
            tier:subscription_tiers(name)
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(org => ({
        id: org?.id,
        name: org?.name,
        subdomain: org?.subdomain,
        logoUrl: org?.logo_url,
        isActive: org?.is_active,
        createdAt: org?.created_at,
        industryName: org?.industry?.name || 'Not specified',
        subscriptionStatus: org?.subscription?.[0]?.status || 'inactive',
        subscriptionTier: org?.subscription?.[0]?.tier?.name || 'Free'
      })) || [];
    } catch (error) {
      console.error('Error fetching all organizations:', error);
      throw error;
    }
  },

  /**
   * Create new organization
   * @param {Object} orgData - Organization data
   * @returns {Promise<Object>} Created organization
   */
  async createOrganization(orgData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('tenants')
        ?.insert({
          name: orgData?.name,
          subdomain: orgData?.subdomain,
          logo_url: orgData?.logoUrl || null,
          primary_color: orgData?.primaryColor || '#3B82F6',
          secondary_color: orgData?.secondaryColor || '#8B5CF6',
          industry_id: orgData?.industryId || null,
          is_active: true,
          settings: orgData?.settings || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        subdomain: data?.subdomain,
        logoUrl: data?.logo_url,
        isActive: data?.is_active,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  /**
   * Update organization details
   * @param {string} tenantId - Organization ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated organization
   */
  async updateOrganization(tenantId, updates) {
    try {
      const updateData = {};
      
      if (updates?.name !== undefined) updateData.name = updates?.name;
      if (updates?.subdomain !== undefined) updateData.subdomain = updates?.subdomain;
      if (updates?.logoUrl !== undefined) updateData.logo_url = updates?.logoUrl;
      if (updates?.primaryColor !== undefined) updateData.primary_color = updates?.primaryColor;
      if (updates?.secondaryColor !== undefined) updateData.secondary_color = updates?.secondaryColor;
      if (updates?.industryId !== undefined) updateData.industry_id = updates?.industryId;
      if (updates?.settings !== undefined) updateData.settings = updates?.settings;

      const { data, error } = await supabase
        ?.from('tenants')
        ?.update(updateData)
        ?.eq('id', tenantId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        subdomain: data?.subdomain,
        logoUrl: data?.logo_url,
        isActive: data?.is_active
      };
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  },

  /**
   * Delete organization (with safeguards)
   * @param {string} tenantId - Organization ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteOrganization(tenantId) {
    try {
      // Check if organization has active members
      const { count: memberCount } = await supabase
        ?.from('user_organization_memberships')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tenant_id', tenantId);

      if (memberCount > 0) {
        throw new Error(`Cannot delete organization with ${memberCount} active member(s). Remove all members first.`);
      }

      const { error } = await supabase
        ?.from('tenants')
        ?.delete()
        ?.eq('id', tenantId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  },

  /**
   * Suspend organization (deactivate)
   * @param {string} tenantId - Organization ID
   * @returns {Promise<boolean>} Success status
   */
  async suspendOrganization(tenantId) {
    try {
      const { error } = await supabase
        ?.from('tenants')
        ?.update({ is_active: false })
        ?.eq('id', tenantId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error suspending organization:', error);
      throw error;
    }
  },

  /**
   * Activate organization
   * @param {string} tenantId - Organization ID
   * @returns {Promise<boolean>} Success status
   */
  async activateOrganization(tenantId) {
    try {
      const { error } = await supabase
        ?.from('tenants')
        ?.update({ is_active: true })
        ?.eq('id', tenantId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error activating organization:', error);
      throw error;
    }
  },

  /**
   * Transfer organization ownership
   * @param {string} tenantId - Organization ID
   * @param {string} newOwnerId - New owner user ID
   * @returns {Promise<boolean>} Success status
   */
  async transferOwnership(tenantId, newOwnerId) {
    try {
      // Update user profile to set new owner's tenant
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ tenant_id: tenantId })
        ?.eq('id', newOwnerId);

      if (profileError) throw profileError;

      // Set as primary membership
      const { error: membershipError } = await supabase
        ?.from('user_organization_memberships')
        ?.update({ is_primary: true })
        ?.eq('user_id', newOwnerId)
        ?.eq('tenant_id', tenantId);

      if (membershipError) throw membershipError;

      return true;
    } catch (error) {
      console.error('Error transferring ownership:', error);
      throw error;
    }
  },

  /**
   * Bulk suspend organizations
   * @param {Array<string>} tenantIds - Array of organization IDs
   * @returns {Promise<Object>} Results with success/failure counts
   */
  async bulkSuspendOrganizations(tenantIds) {
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.update({ is_active: false })
        ?.in('id', tenantIds)
        ?.select();

      if (error) throw error;

      return {
        success: data?.length || 0,
        failed: tenantIds?.length - (data?.length || 0)
      };
    } catch (error) {
      console.error('Error bulk suspending organizations:', error);
      throw error;
    }
  },

  /**
   * Bulk activate organizations
   * @param {Array<string>} tenantIds - Array of organization IDs
   * @returns {Promise<Object>} Results with success/failure counts
   */
  async bulkActivateOrganizations(tenantIds) {
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.update({ is_active: true })
        ?.in('id', tenantIds)
        ?.select();

      if (error) throw error;

      return {
        success: data?.length || 0,
        failed: tenantIds?.length - (data?.length || 0)
      };
    } catch (error) {
      console.error('Error bulk activating organizations:', error);
      throw error;
    }
  },

  /**
   * Bulk delete organizations
   * @param {Array<string>} tenantIds - Array of organization IDs
   * @returns {Promise<Object>} Results with success/failure counts
   */
  async bulkDeleteOrganizations(tenantIds) {
    try {
      let successCount = 0;
      let failedCount = 0;
      const errors = [];

      for (const tenantId of tenantIds) {
        try {
          await this.deleteOrganization(tenantId);
          successCount++;
        } catch (err) {
          failedCount++;
          errors?.push({ tenantId, error: err?.message });
        }
      }

      return {
        success: successCount,
        failed: failedCount,
        errors
      };
    } catch (error) {
      console.error('Error bulk deleting organizations:', error);
      throw error;
    }
  },

  /**
   * Get organization health metrics
   * @param {string} tenantId - Organization ID
   * @returns {Promise<Object>} Health metrics
   */
  async getOrganizationHealth(tenantId) {
    try {
      // Get member count
      const { count: memberCount } = await supabase
        ?.from('user_organization_memberships')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tenant_id', tenantId);

      // Get active members (logged in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      
      const { count: activeMembers } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tenant_id', tenantId)
        ?.gte('last_login_at', thirtyDaysAgo?.toISOString());

      // Get subscription status
      const { data: subscription } = await supabase
        ?.from('tenant_subscriptions')
        ?.select('status, tier:subscription_tiers(name)')
        ?.eq('tenant_id', tenantId)
        ?.single();

      return {
        totalMembers: memberCount || 0,
        activeMembers: activeMembers || 0,
        activityRate: memberCount > 0 ? Math.round((activeMembers / memberCount) * 100) : 0,
        subscriptionStatus: subscription?.status || 'inactive',
        subscriptionTier: subscription?.tier?.name || 'Free',
        healthScore: this.calculateHealthScore({
          memberCount: memberCount || 0,
          activeMembers: activeMembers || 0,
          subscriptionStatus: subscription?.status
        })
      };
    } catch (error) {
      console.error('Error fetching organization health:', error);
      throw error;
    }
  },

  /**
   * Calculate organization health score
   * @private
   */
  calculateHealthScore({ memberCount, activeMembers, subscriptionStatus }) {
    let score = 0;

    // Member count contribution (0-40 points)
    if (memberCount > 0) score += Math.min(memberCount * 5, 40);

    // Activity rate contribution (0-40 points)
    if (memberCount > 0) {
      const activityRate = activeMembers / memberCount;
      score += activityRate * 40;
    }

    // Subscription status contribution (0-20 points)
    const statusPoints = {
      active: 20,
      trial: 15,
      inactive: 5,
      suspended: 0,
      cancelled: 0
    };
    score += statusPoints?.[subscriptionStatus] || 0;

    return Math.min(Math.round(score), 100);
  },

  /**
   * Create a new member in an organization
   * @param {Object} memberData - Member creation data
   * @param {string} memberData.email - Member email
   * @param {string} memberData.firstName - Member first name
   * @param {string} memberData.lastName - Member last name
   * @param {string} memberData.roleId - Role ID to assign
   * @param {string} memberData.tenantId - Organization/tenant ID
   * @param {string} memberData.jobTitle - Optional job title
   * @returns {Promise<Object>} Creation result
   */
  async createMember(memberData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.rpc('create_organization_member', {
          p_email: memberData?.email,
          p_first_name: memberData?.firstName,
          p_last_name: memberData?.lastName,
          p_role_id: memberData?.roleId,
          p_tenant_id: memberData?.tenantId,
          p_invited_by: user?.id,
          p_job_title: memberData?.jobTitle || null
        });

      if (error) throw error;

      return {
        success: data?.success,
        userId: data?.user_id,
        isInvitation: data?.is_invitation,
        message: data?.message
      };
    } catch (error) {
      console.error('Error creating member:', error);
      throw error;
    }
  },

  /**
   * Check if current user can invite to a specific role
   * @param {string} roleId - Target role ID
   * @param {string} tenantId - Organization/tenant ID
   * @returns {Promise<boolean>} Whether user can invite to this role
   */
  async canInviteToRole(roleId, tenantId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        return false;
      }

      const { data, error } = await supabase
        ?.rpc('can_invite_to_role', {
          p_inviter_user_id: user?.id,
          p_target_role_id: roleId,
          p_tenant_id: tenantId
        });

      if (error) throw error;

      return data === true;
    } catch (error) {
      console.error('Error checking invite permission:', error);
      return false;
    }
  }
};

export default organizationService;